import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Database, Globe, Shield } from 'lucide-react';

const AboutLeiSystemPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Button */}
                <div className="mb-6">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Home
                    </Link>
                </div>

                {/* Page Header */}
                <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8 mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <Database className="h-8 w-8 text-primary-600" />
                        <h1 className="text-3xl font-bold text-gray-900">The LEI System</h1>
                    </div>
                    <p className="text-lg text-gray-600">
                        Understanding the Global Legal Entity Identifier System and its importance
                        in financial markets
                    </p>
                </div>

                {/* Content */}
                <div className="space-y-8">
                    {/* What is the LEI system */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Globe className="h-6 w-6 text-primary-600" />
                            <h2 className="text-2xl font-semibold text-gray-900">
                                What is the LEI system?
                            </h2>
                        </div>
                        <div className="prose max-w-none">
                            <p className="text-gray-700 leading-relaxed mb-4">
                                The Global Legal Entity Identifier System (sometime abbreviated to
                                GLEIS) is designed to address the problems of corporate identity,
                                particularly in the financial markets. Names of corporate entities
                                are very unreliable ways of identifying them, as many discovered to
                                their cost in the wake of the collapse of Lehman Brothers, and the
                                financial crisis.
                            </p>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                The names are often abbreviated, the same legal entity often changes
                                name multiple times, names are reused, and entities can even have
                                multiple legal names (in different languages). For this reason, it
                                is useful to assign a persistent identifier to each corporate
                                entity, unconnected with its name.
                            </p>
                            <p className="text-gray-700 leading-relaxed font-medium">
                                This identifier is called an LEI (Legal Entity Identifier)
                            </p>
                        </div>
                    </div>

                    {/* Who is behind the LEI system */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Shield className="h-6 w-6 text-primary-600" />
                            <h2 className="text-2xl font-semibold text-gray-900">
                                Who is behind the LEI system?
                            </h2>
                        </div>
                        <div className="prose max-w-none">
                            <p className="text-gray-700 leading-relaxed mb-4">
                                The LEI system is being set up at the behest of the G20, who tasked
                                the Financial Stability Board with the job of creating it. It will
                                be a federated system, with entities being issued by Local Operating
                                Units (LOUs), overseen by a Central Operating Unit, which will
                                coordinate their work and the data in the system. This in turn will
                                be an arm of the Global Legal Entity Identifier Foundation, which is
                                currently being set up by the Financial Stability Board.
                            </p>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                There is also the Regulatory Oversight Committee (ROC), which is
                                made up of government financial regulators, and will oversee the
                                whole system. More information on the ROC and the formal structure
                                of the GLEIS can be found at{' '}
                                <a
                                    href="https://www.gleif.org/en/about-lei/regulatory-oversight-committee-roc-charter"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                >
                                    The LEI ROC website
                                </a>
                            </p>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-blue-900 font-medium">
                                    A central principle of the LEI system is that the identifiers,
                                    and the data within the system, are free to use without any IP
                                    restrictions.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Pre-LOUs explanation */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                            If the Foundation and Central Operating Unit hasn't been set up yet,
                            what are the LEIs on Open LEIs?
                        </h2>
                        <div className="prose max-w-none">
                            <p className="text-gray-700 leading-relaxed mb-4">
                                While the GLEIS is still being formed, some regulators have already
                                begun to require market participants to have LEIs. These are being
                                issued by so-called pre-LOUs (Local Operating Units), the federated
                                bodies who will actually be registering entities in the LEI system.
                            </p>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                These LEIs match the format of the LEI, and can work as basic
                                identifiers, although some are missing required fields, and there
                                are data compatibility and integrity issues. By providing a 'live'
                                dataset they are also performing the valuable role of helping
                                identify quality, structure and data problems with the system
                            </p>
                        </div>
                    </div>

                    {/* Call to action */}
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-8 text-center">
                        <h3 className="text-xl font-semibold text-primary-900 mb-4">
                            Ready to explore LEI data?
                        </h3>
                        <p className="text-primary-700 mb-6">
                            Start searching through millions of Legal Entity Identifiers from around
                            the world.
                        </p>
                        <Link
                            to="/search"
                            className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                        >
                            <Database className="h-5 w-5" />
                            Search LEI Database
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AboutLeiSystemPage;
